/**
 * CollapsibleSection — labelled "twistie" wrapping `Collapsible`.
 *
 * Standardises the chevron + clickable header + content structure that
 * was previously rebuilt inline at 5+ sites in the app. Use this for
 * "Advanced" reveal-on-demand sections inside forms or detail panels.
 *
 * Not suitable for full FAQ/accordion stacks (use `Accordion`) or for
 * page-level tabbed content (use `Tabs`).
 */

import { useState } from "react";
import { ChevronRight } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../primitives/collapsible";
import { cn } from "../primitives/utils";

export interface CollapsibleSectionProps {
  /** Header text. Required. */
  title: React.ReactNode;
  /** Secondary text rendered next to the title in a muted style. */
  hint?: React.ReactNode;
  /** Initial open state. Defaults to closed. */
  defaultOpen?: boolean;
  /** Controlled open state. Pass with `onOpenChange` for external control. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Body content. */
  children: React.ReactNode;
  /** Extra classes for the wrapper. */
  className?: string;
  /**
   * `"inline"` (default) is the original appearance — a muted-foreground
   * title, used for "Advanced" reveal-on-demand sections inside forms
   * (ConfigDefinitionEditor). `"page-section"` is for a
   * long page's own top-level section headers (JobWorkspacePage): the title
   * renders in `text-primary` with more vertical padding so sections read as
   * separate at a glance. Only the title colour/spacing changes — the
   * chevron and the muted `hint` stay as they are in both variants.
   */
  variant?: "inline" | "page-section";
}

export function CollapsibleSection({
  title,
  hint,
  defaultOpen = false,
  open: controlledOpen,
  onOpenChange,
  children,
  className,
  variant = "inline",
}: CollapsibleSectionProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : uncontrolledOpen;
  const setOpen = (next: boolean) => {
    if (!isControlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setOpen}
      className={cn("border-t border-border", className)}
    >
      <CollapsibleTrigger
        className={cn(
          "w-full flex items-center gap-2 text-sm font-medium text-left",
          "text-muted-foreground hover:text-foreground",
          "focus:outline-none focus:ring-2 focus:ring-primary rounded",
          variant === "page-section" ? "py-3" : "py-2",
        )}
      >
        <ChevronRight
          className={cn(
            "size-3.5 transition-transform shrink-0",
            isOpen && "rotate-90",
          )}
          aria-hidden
        />
        {/* Only the title's colour changes for `page-section` — the chevron
            and the trigger's own (muted) colour classes stay untouched, so
            they keep inheriting the inline appearance. */}
        <span
          className={variant === "page-section" ? "text-primary" : undefined}
        >
          {title}
        </span>
        {hint ? (
          <span className="text-xs font-normal text-muted-foreground">
            {hint}
          </span>
        ) : null}
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-2 pb-3">{children}</CollapsibleContent>
    </Collapsible>
  );
}
